import React, { useState, useEffect, useContext } from 'react';
import './App.css';
import './css/TabToggle.css';
import Header from "./components/Header";
import Footer from "./components/Footer";
import ApiKeyForm from "./components/ApiKeyForm";
import ApiKeyDisplay from "./components/ApiKeyDisplay";
import ApiTable from "./components/ApiTable";
import createHash from "./utils/createHash";
import { Box, ToggleButton, ToggleButtonGroup, Typography, CircularProgress } from '@mui/material';
import SpauthServiceContext from './contexts/SpauthContext';
import EnvironmentBanner from './components/EnvironmentBanner';

function App() {
    const [activeTab, setActiveTab] = useState('getKey');
    const [slidePosition, setSlidePosition] = useState(0);

    const [formData, setFormData] = useState({
        publisherName: '',
        accounts: '',
        rank: '',
        name: '',
        email: '',
        labelAlias: '',
        labelText: ''
    });

    const [generatedApiKey, setGeneratedApiKey] = useState(null);
    const [spauthToken, setSpauthToken] = useState(null);
    const [saveSteps, setSaveSteps] = useState(null);

    const contextValue = useContext(SpauthServiceContext);

    // Update spauthToken from context when available
    useEffect(() => {
        if (contextValue && contextValue.token) {
            setSpauthToken(contextValue.token);
        }
    }, [contextValue]);

    // Update slide position when active tab changes
    useEffect(() => {
        setSlidePosition(activeTab === 'getKey' ? 0 : 1);
    }, [activeTab]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'publisherName') {
                updated.labelAlias = value.replace(/[\s-]/g, '_').toUpperCase();
                updated.labelText = value.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            }
            return updated;
        });
    };

    const setStep = (key, status, message = '') =>
        setSaveSteps(prev => ({ ...prev, [key]: { status, message } }));

    async function callApi() {
        setGeneratedApiKey(null);
        setSaveSteps({
            dynamo: { status: 'loading', message: '' },
            label:  { status: 'idle',    message: '' }
        });

        const apiKey = crypto.randomUUID();
        const hashedKey = await createHash(apiKey);

        const requestBody = {
            hashedKey,
            team: formData.publisherName,
            rank: formData.rank,
            accountIds: formData.accounts
                .split(/[\n,]+/)
                .map(account => account.trim())
                .filter(account => account.length > 0),
            createdBy: formData.name,
            createdAt: Date.now(),
            createdByEmail: formData.email,
            spauthUsername: contextValue.username
        };

        try {
            const dynamoRes = await fetch("/dynamo/", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'foo': spauthToken },
                body: JSON.stringify(requestBody)
            });

            if (!dynamoRes.ok) {
                setStep('dynamo', 'error', `Failed (${dynamoRes.status})`);
                return;
            }
            setStep('dynamo', 'success');
            setStep('label', 'loading');
            const locales = ['en_US', 'es_US', 'fr_CA', 'en_CA'];

            const labelBody = locales.map(locale => ({
                alias: formData.labelAlias,
                appName: null,
                text: formData.labelText,
                locale,
                spauthUsername: contextValue.username
            }));

            const labelRes = await fetch("/label-hollow/labels/list", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `SPAUTH ${spauthToken}`
                },
                body: JSON.stringify(labelBody)
            });

            if (!labelRes.ok) {
                setStep('label', 'error', `Failed (${labelRes.status})`);
            } else {
                setStep('label', 'success');
                setGeneratedApiKey(apiKey);
            }
        } catch (error) {
            console.log('Caught error:', error);
        }
    }

    const handleCloseApiKey = () => {
        setGeneratedApiKey(null);
    };

    const handleTabChange = (event, newTab) => {
        if (newTab !== null) {
            setActiveTab(newTab);
        }
    };

    return (
        <div className="App" style={{ paddingBottom: '64px' }}>
            <EnvironmentBanner />
            <Header />
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 3, marginBottom: 2 }}>
                <div className="tab-toggle-container">
                    <ToggleButtonGroup
                        value={activeTab}
                        exclusive
                        onChange={handleTabChange}
                        aria-label="navigation tabs"
                        className={`tab-toggle-group ${slidePosition === 1 ? 'slide-right' : ''}`}
                    >
                        <ToggleButton
                            value="getKey"
                            aria-label="get api key"
                            className="tab-toggle-button"
                        >
                            Create New Key
                        </ToggleButton>
                        <ToggleButton
                            value="showTable"
                            aria-label="show api table"
                            className="tab-toggle-button"
                        >
                            API Key Table
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </Box>
            {activeTab === 'getKey' ? (
                <>
                    <ApiKeyForm
                        formData={formData}
                        handleInputChange={handleInputChange}
                        onSubmit={callApi}
                    />
                    {saveSteps && (
                        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, py: 1.5, display: 'flex', gap: 3, justifyContent: 'center' }}>
                            {[
                                { key: 'dynamo', label: 'Saving API key to database' },
                                { key: 'label',  label: 'Saving label translation'   }
                            ].map(({ key, label }) => {
                                const step = saveSteps[key];
                                const icon = step.status === 'loading' ? <CircularProgress size={16} />
                                           : step.status === 'success' ? '✓'
                                           : step.status === 'error'   ? '✗'
                                           : '–';
                                const color = step.status === 'success' ? 'green'
                                            : step.status === 'error'   ? 'red'
                                            : 'text.secondary';
                                return (
                                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 20, display: 'flex', justifyContent: 'center', color }}>{icon}</Box>
                                        <Typography variant="body2" sx={{ color }}>
                                            {label}{step.message ? ` — ${step.message}` : ''}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    {generatedApiKey && <ApiKeyDisplay apiKey={generatedApiKey} labelAlias={formData.labelAlias} onClose={handleCloseApiKey} />}
                </>
            ) : (
                <ApiTable/>
            )}
            <Footer />
        </div>
    );
}

export default App;
